import React from 'react'
import { useState } from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { type DraftCard } from '../../store/bulk-import.store.types'

import { InlinePopoverEditor } from './InlinePopoverEditor'
import { StandardsBasedCriteriaEditor } from './StandardsBasedCriteriaEditor'

import { useRubric } from '@/hooks/useRubrics'
import RubricDialog from '@/components/RubricBuilder'

interface EvaluationCriteriaProps {
  card: DraftCard
  updateCard: (cardId: string, field: keyof DraftCard, value: any) => void
}

export function EvaluationCriteria({ card, updateCard }: EvaluationCriteriaProps) {
  const gradingMethodRubric = card.grading_method === 'rubric'
  const gradingMethodPassFail = card.grading_method === 'simple'
  const gradingMethodStandardsBased = card.grading_method === 'standards_based'

  if (gradingMethodRubric) {
    return <EvaluationCriteriaRubric card={card} updateCard={updateCard} />
  } else if (gradingMethodPassFail) {
    return (
      <InlinePopoverEditor
        label={card.grading_criteria || 'Click to edit criteria'}
        placeholder="Enter evaluation criteria here..."
        value={card.grading_criteria}
        onSave={newCriteria => {
          updateCard(card.id, 'grading_criteria', newCriteria)
        }}
      />
    )
  } else if (gradingMethodStandardsBased) {
    return <StandardsBasedCriteriaEditor card={card} updateCard={updateCard} />
  }

  // Fallback for other grading methods or if criteria are not applicable
  return (
    <Typography color="text.secondary" variant="body2">
      N/A
    </Typography>
  )
}

interface EvaluationCriteriaRubricProps {
  card: DraftCard
  updateCard: (cardId: string, field: keyof DraftCard, value: any) => void
}

function EvaluationCriteriaRubric({ card, updateCard }: EvaluationCriteriaRubricProps) {
  const [isRubricDialogOpen, setIsRubricDialogOpen] = useState(false)
  const { defaultRubricId } = useSelector((state: any) => state.user.profile)

  const rubricId = card?.rubricId || defaultRubricId || 'SuGg3NZlLYOTcLPrPlyc'
  const { rubric } = useRubric({
    id: rubricId,
    enabled: true,
  })

  const onSaveRubric = (selectedRubric: any) => {
    const total_points = selectedRubric.total_points
    const passing_score = Math.floor(selectedRubric?.total_points / 2)

    updateCard(card.id, 'points', total_points)
    updateCard(card.id, 'passing_score', passing_score)
    updateCard(card.id, 'rubricId', selectedRubric.id)
  }

  return (
    <>
      <RubricDialog
        isOnlyOneSave
        open={isRubricDialogOpen}
        rubricId={rubricId}
        onClose={() => {
          setIsRubricDialogOpen(false)
        }}
        // @ts-ignore
        onSave={(_, selectedRubricArgument) => {
          onSaveRubric(selectedRubricArgument)
          setIsRubricDialogOpen(false)
        }}
      />
      <Stack
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          setIsRubricDialogOpen(true)
        }}
      >
        <Tooltip disableFocusListener={!rubric?.name} title={rubric?.name || 'N/A'}>
          <Typography
            noWrap
            fontSize={14}
            fontWeight={500}
            maxWidth={250}
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            {rubric?.name || 'N/A'}
          </Typography>
        </Tooltip>
      </Stack>
    </>
  )
}
